import { ReactNode, SyntheticEvent } from 'react';
import { CoreBox, CoreCheckbox, CoreFormControlLabel, CoreTypography } from '@youscience/core';

import {
  REST_OF_US_FILTER,
  REST_OF_US_LABEL,
  STATE_PUBLIC_COLLEGES_SUBTITLE,
  STATE_PUBLIC_COLLEGE_FILTER,
  getRestOfUsFilter,
  getStatePublicCollegeFilter,
  getStatePublicCollegesLabel,
  getStatePublicCollegesNotice,
} from '@constants/stateSpotlight';
import { StateSpotlight } from '@interfaces/stateSpotlight';
import { UsheIcon } from '@components/UsheIcon';

import InfoIcon from '@images/info.svg?react';

import { sxStyles } from './StateSpotlight.styles';

export type StateSpotlightLocationKey = typeof STATE_PUBLIC_COLLEGE_FILTER | typeof REST_OF_US_FILTER;

interface LocationBucketOptionProps {
  testId: string;
  icon?: ReactNode;
  label: string;
  count?: number;
  subtitle?: string;
  checked: boolean;
  onChange: (event: SyntheticEvent, checked: boolean) => void;
}

const LocationBucketOption = ({
  testId,
  icon,
  label,
  count,
  subtitle,
  checked,
  onChange,
}: LocationBucketOptionProps) => (
  <CoreFormControlLabel
    data-testid={testId}
    sx={sxStyles.filterOption}
    control={<CoreCheckbox checked={checked} onChange={onChange} />}
    label={
      <CoreBox sx={sxStyles.filterOptionLabel}>
        <CoreBox sx={sxStyles.filterOptionTitle}>
          {icon}
          <CoreTypography component="span" fontWeight={600}>
            {label}
          </CoreTypography>
          {count !== undefined && (
            <CoreTypography component="span" variant="body2" sx={sxStyles.filterOptionCount}>
              {count}
            </CoreTypography>
          )}
        </CoreBox>
        {subtitle && (
          <CoreTypography variant="caption" sx={sxStyles.filterOptionSubtitle}>
            {subtitle}
          </CoreTypography>
        )}
      </CoreBox>
    }
  />
);

interface StateSpotlightLocationOptionsProps {
  spotlight: StateSpotlight;
  filters: Record<string, unknown>;
  onChange: (key: StateSpotlightLocationKey, checked: boolean) => void;
}

export const StateSpotlightLocationOptions = ({ spotlight, filters, onChange }: StateSpotlightLocationOptionsProps) => (
  <>
    <LocationBucketOption
      testId="statePublicCollegeCheckbox"
      icon={<UsheIcon />}
      label={getStatePublicCollegesLabel(spotlight.stateName)}
      count={spotlight.publicCollegeIds.length}
      subtitle={STATE_PUBLIC_COLLEGES_SUBTITLE}
      checked={!!getStatePublicCollegeFilter(filters)}
      onChange={(event, checked) => onChange(STATE_PUBLIC_COLLEGE_FILTER, checked)}
    />
    <LocationBucketOption
      testId="restOfUsCheckbox"
      label={REST_OF_US_LABEL}
      checked={getRestOfUsFilter(filters)}
      onChange={(event, checked) => onChange(REST_OF_US_FILTER, checked)}
    />
  </>
);

export const getStateSpotlightLocationValue = (key: StateSpotlightLocationKey, spotlight: StateSpotlight) =>
  key === STATE_PUBLIC_COLLEGE_FILTER ? [spotlight.stateCode] : ['true'];

export const StatePublicCollegesNotice = ({ spotlight }: { spotlight: StateSpotlight }) => (
  <CoreBox data-testid="statePublicCollegeNotice" sx={sxStyles.notice}>
    <CoreBox component="span" sx={sxStyles.icon}>
      <InfoIcon />
    </CoreBox>
    <CoreTypography variant="body2">{getStatePublicCollegesNotice(spotlight.stateName)}</CoreTypography>
  </CoreBox>
);
